<template>
  <v-container class="pa-4 pa-sm-8" fluid>
    <v-overlay :value="btnloading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <h2 class="dark--text text-h5 font-weight-bold">Sales Detailed Report</h2>
    <v-row>
      <v-col cols="6" sm="3" md="2" class="mt-3">
        <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="displayStartDate"
              label="Start Date"
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              solo
            ></v-text-field>
          </template>
          <v-date-picker v-model="startDate" @input="onStartDateSelected" :max="maxDate" no-title scrollable></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="6" sm="3" md="2" class="mt-3">
        <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="displayEndDate"
              label="End Date"
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              solo
            ></v-text-field>
          </template>
          <v-date-picker v-model="endDate" @input="onEndDateSelected" :min="startDate" :max="maxDate" no-title scrollable></v-date-picker>
        </v-menu>
      </v-col>
      <v-col v-if="dateRangeError" cols="12" class="py-0">
        <v-alert type="error" dense dismissible>{{ dateRangeError }}</v-alert>
      </v-col>
      <v-col cols="5" sm="3" md="2" class="mt-3">
        <v-select
          v-model="filterType"
          :items="filterOptions"
          item-text="text"
          item-value="value"
          dense
          solo
          :menu-props="{ contentClass: 'filter-type-menu' }"
          @change="searchQuery = ''"
        ></v-select>
      </v-col>
      <v-col cols="7" sm="4" md="3" class="mt-3">
        <v-text-field
          v-model="searchQuery"
          :label="'Search ' + filterLabel + '...'"
          prepend-inner-icon="mdi-magnify"
          dense
          solo
          clearable
        ></v-text-field>
      </v-col>
      <v-col class="text-end mt-3">
        <ExportDetailedSalesReport :flatRows="filteredRows" />
      </v-col>
    </v-row>
    <v-data-table
      :mobile-breakpoint="0"
      id="myTable"
      :headers="headers"
      :items="filteredRows"
      :loading="loading"
      item-key="slNo"
      :items-per-page="25"
      class="elevation-1 mt-5 mt-sm-6 dark--text"
    >
      <template v-slot:body="props">
        <tbody>
          <tr v-for="item in props.items" :key="item.slNo">
            <td>{{ item.slNo }}</td>
            <td>{{ item.salesOrderNo }}</td>
            <td>{{ item.clientName }}</td>
            <td>{{ item.customerAccount }}</td>
            <td>{{ item.phone }}</td>
            <td>{{ item.deliveryDate }}</td>
            <td>{{ item.programDay }}</td>
            <td>{{ item.totalPackageDays }}</td>
            <td>{{ item.grossSalesAmount }}</td>
            <td>{{ item.discountAmount }}</td>
            <td>{{ item.netSalesAmount }}</td>
            <td>{{ item.programName }}</td>
            <td>{{ item.gender }}</td>
            <td>{{ item.deliveryLocation }}</td>
            <td>{{ item.clinicName }}</td>
            <td>{{ item.promoCode }}</td>
            <td>{{ item.referralId }}</td>
            <td>{{ item.createdDate }}</td>
            <td>{{ item.createdBy }}</td>
          </tr>
          <tr style="background-color: #ec3326; color: white;">
            <td></td>
            <td></td>
            <td>Total</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td>{{ totalGross }}</td>
            <td>{{ totalDiscount }}</td>
            <td>{{ totalNet }}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import moment from "moment";
import ExportDetailedSalesReport from "../components/ExportDetailedSalesReport.vue";

export default {
  components: { ExportDetailedSalesReport },
  created() {
    if (!this.hasRole(this.AdminUserPermissions.SALES_REPORT_VIEW)) {
      this.$router.push({ name: "Error404" });
    }
    const today = moment().format("YYYY-MM-DD");
    this.startDate = today;
    this.endDate = today;
    this.fetchSalesReport();
  },
  methods: {
    onStartDateSelected() {
      this.startDateMenu = false;
      if (this.endDate && moment(this.endDate).isBefore(this.startDate)) {
        this.endDate = this.startDate;
      }
      this.fetchSalesReport();
    },
    onEndDateSelected() {
      this.endDateMenu = false;
      this.fetchSalesReport();
    },
    fetchSalesReport() {
      if (!this.startDate || !this.endDate) return;
      const diff = moment(this.endDate).diff(moment(this.startDate), "days");
      if (diff < 0) {
        this.dateRangeError = "End date must be on or after start date.";
        return;
      }
      if (diff > 31) {
        this.dateRangeError = "Date range cannot exceed 31 days.";
        return;
      }
      this.dateRangeError = "";
      this.variables.startDate = this.startDate;
      this.variables.endDate = this.endDate;
      this.loading = true;
      this.$apollo
        .query({
          query: require("../api/getDetailedSalesReport.graphql"),
          variables: this.variables,
          fetchPolicy: "network-only",
        })
        .then(data => {
          this.loading = false;
          this.btnloading = false;
          this.salesReport = data.data.getDetailedSalesReport;
          this.flatRows = this.computeFlatRows();
        })
        .catch(error => {
          this.loading = false;
          console.log(error);
        });
    },
    computeFlatRows() {
      let rows = [];
      const rangeStart = moment(this.startDate, "YYYY-MM-DD").startOf("day");
      const rangeEnd = moment(this.endDate, "YYYY-MM-DD").endOf("day");

      this.salesReport.forEach(item => {
        if (!item.salesOrder || !item.user) return;
        let clientName =
          (item.user.firstnameEn ? item.user.firstnameEn[0].toUpperCase() + item.user.firstnameEn.slice(1) : "") +
          " " +
          (item.user.lastnameEn ? item.user.lastnameEn[0].toUpperCase() + item.user.lastnameEn.slice(1) : "");
        clientName = clientName.trim();
        let gender = item.user.gender || "";
        let phone = item.user.phone ? String(item.user.phone) : "";

        item.salesOrder.forEach(order => {
          if (!order.package || !order.package.availableChoices || !order.package.availableChoices.length) return;

          let choice = order.package.availableChoices[0];
          let availableNumOfDays = choice.availableNumOfDays;
          if (!availableNumOfDays) return;

          let orderStart = moment(new Date(parseInt(order.start_date))).startOf("day");
          let orderEnd = moment(new Date(parseInt(order.end_date))).endOf("day");

          const pausedDates = new Set();
          if (order.selected_menu) {
            order.selected_menu.forEach(sm => {
              if (sm.is_paused) {
                pausedDates.add(moment(new Date(parseInt(sm.date))).format("YYYY-MM-DD"));
              }
            });
          }

          let dailyGross = order.package_price / availableNumOfDays;
          let dailyDiscount = order.discount / availableNumOfDays;
          let dailyNet = order.net_price / availableNumOfDays;

          let promoCode = (order.promoCodeInfo && order.promoCodeInfo.promoCodeDetails && order.promoCodeInfo.promoCodeDetails.promoCode)
            ? order.promoCodeInfo.promoCodeDetails.promoCode
            : "-";
          let referralId = promoCode !== "-" ? promoCode : "No Discount";
          let createdDate = order.created_At
            ? moment(new Date(parseInt(order.created_At))).format("DD/MM/YYYY")
            : "";

          let dateCursor = moment(rangeStart);
          while (dateCursor.isSameOrBefore(rangeEnd, "day")) {
            let currentDate = dateCursor.clone();

            if (!currentDate.isSameOrAfter(orderStart, "day") || !currentDate.isSameOrBefore(orderEnd, "day")) {
              dateCursor.add(1, "day");
              continue;
            }
            if (!choice.availableDays.includes(currentDate.format("dddd"))) {
              dateCursor.add(1, "day");
              continue;
            }
            if (pausedDates.has(currentDate.format("YYYY-MM-DD"))) {
              dateCursor.add(1, "day");
              continue;
            }

            let programDay = 0;
            let dayCursor = moment(orderStart);
            while (dayCursor.isSameOrBefore(currentDate, "day")) {
              let wd = dayCursor.format("dddd");
              if (choice.availableDays.includes(wd) && !pausedDates.has(dayCursor.format("YYYY-MM-DD"))) {
                programDay++;
              }
              dayCursor.add(1, "day");
            }

            rows.push({
              salesOrderNo: order.order_number || "",
              clientName,
              customerAccount: item.customerId || "",
              phone,
              deliveryDate: currentDate.format("DD/MM/YYYY"),
              programDay,
              totalPackageDays: availableNumOfDays,
              grossSalesAmount: dailyGross.toFixed(3),
              discountAmount: dailyDiscount.toFixed(3),
              netSalesAmount: dailyNet.toFixed(3),
              programName: order.package.titleEn || "",
              gender,
              deliveryLocation: item.areaName || "",
              clinicName: "",
              promoCode,
              referralId,
              createdDate,
              createdBy: (order.createdByName || "").trim(),
            });

            dateCursor.add(1, "day");
          }
        });
      });

      rows.sort((a, b) => a.clientName.localeCompare(b.clientName));
      return rows.map((row, index) => ({ ...row, slNo: index + 1 }));
    },
  },
  computed: {
    displayStartDate() {
      return this.startDate ? moment(this.startDate, "YYYY-MM-DD").format("DD/MM/YYYY") : "";
    },
    displayEndDate() {
      return this.endDate ? moment(this.endDate, "YYYY-MM-DD").format("DD/MM/YYYY") : "";
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    },
    filterLabel() {
      const opt = this.filterOptions.find(o => o.value === this.filterType);
      return opt ? opt.text : "";
    },
    filteredRows() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.flatRows;
      const q = this.searchQuery.trim().toLowerCase();
      return this.flatRows
        .filter(row => String(row[this.filterType] || "").toLowerCase().includes(q))
        .map((row, index) => ({ ...row, slNo: index + 1 }));
    },
    totalGross() {
      return this.filteredRows.reduce((sum, row) => sum + parseFloat(row.grossSalesAmount), 0).toFixed(3);
    },
    totalDiscount() {
      return this.filteredRows.reduce((sum, row) => sum + parseFloat(row.discountAmount), 0).toFixed(3);
    },
    totalNet() {
      return this.filteredRows.reduce((sum, row) => sum + parseFloat(row.netSalesAmount), 0).toFixed(3);
    },
  },
  data() {
    return {
      salesReport: [],
      flatRows: [],
      startDate: "",
      endDate: "",
      startDateMenu: false,
      endDateMenu: false,
      dateRangeError: "",
      filterType: "clientName",
      filterOptions: [
        { text: "Customer Name", value: "clientName" },
        { text: "Phone", value: "phone" },
        { text: "Customer ID", value: "customerAccount" },
        { text: "Sales Order ID", value: "salesOrderNo" },
      ],
      searchQuery: "",
      loading: true,
      btnloading: true,
      headers: [
        { text: "Sl.No", value: "slNo", width: 55, sortable: false },
        { text: "Sales Order No", value: "salesOrderNo", width: 130, sortable: false },
        { text: "Client Name", value: "clientName", width: 170, sortable: false },
        { text: "Customer Account", value: "customerAccount", width: 140, sortable: false },
        { text: "Phone Number", value: "phone", width: 130, sortable: false },
        { text: "Delivery Date", value: "deliveryDate", width: 120, sortable: false },
        { text: "Availed Package Day", value: "programDay", width: 140, sortable: false },
        { text: "Total Package Days", value: "totalPackageDays", width: 140, sortable: false },
        { text: "Gross Sales Amount", value: "grossSalesAmount", width: 155, sortable: false },
        { text: "Discount Amount", value: "discountAmount", width: 140, sortable: false },
        { text: "Net Sales Amount", value: "netSalesAmount", width: 140, sortable: false },
        { text: "Program Name", value: "programName", width: 180, sortable: false },
        { text: "Gender", value: "gender", width: 100, sortable: false },
        { text: "Delivery Location", value: "deliveryLocation", width: 145, sortable: false },
        { text: "Clinic Name", value: "clinicName", width: 120, sortable: false },
        { text: "Promo Code", value: "promoCode", width: 120, sortable: false },
        { text: "Referral ID", value: "referralId", width: 120, sortable: false },
        { text: "Created Date", value: "createdDate", width: 120, sortable: false },
        { text: "Created By", value: "createdBy", width: 120, sortable: false },
      ],
      variables: {
        startDate: "",
        endDate: "",
      },
    };
  },
};
</script>

<style>
.filter-type-menu .v-list-item--active {
  background-color: #fde8e7 !important;
}
.filter-type-menu .v-list-item--active .v-list-item__title {
  color: #c0392b !important;
}
</style>

<style scoped>
tr.v-data-table__selected {
  background: #7d92f5 !important;
}
table,
th,
td {
  border: 1px solid black;
  border-collapse: collapse;
  padding: 5px;
  text-align: center;
}
</style>